import React, { useEffect, useMemo, useState } from 'react';
import { FilterOptions, FeedbackDashboardMetrics } from '../types';
import {
    PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend,
    BarChart, Bar, XAxis, YAxis, CartesianGrid, LabelList
} from 'recharts';
import {
    Users, TrendingUp, Download, Star, Send, Percent, Award, Target, Grid3X3, BarChart2, LogOut, Building2, RefreshCw
} from 'lucide-react';
import Filters from './Filters';
import Footer from './Footer';
import { useNavigate } from 'react-router-dom';
import { getPartnerName, getWorkshopName } from '../utils/analytics';
import apiClient from '../utils/api';
import LoadingOverlay from './LoadingOverlay';
import { useCache } from '../contexts/CacheContext';
import { PortalLogoLink } from './PortalLogoLink';

// District short names mapping
const districtShortNames: { [key: string]: string } = {
    'Alluri Sitharama Raju': 'ASR',
    'Anakapalli': 'Anakapalli',
    'Ananthapuramu': 'Ananthapuramu',
    'Annamayya': 'Annamayya',
    'Bapatla': 'Bapatla',
    'Chittoor': 'Chittoor',
    'Dr. B.R. Ambedkar Konaseema': 'Konaseema',
    'East Godavari': 'E. Godavari',
    'Eluru': 'Eluru',
    'Guntur': 'Guntur',
    'Kakinada': 'Kakinada',
    'Krishna': 'Krishna',
    'Kurnool': 'Kurnool',
    'Nandyal': 'Nandyal',
    'NTR': 'NTR',
    'Palnadu': 'Palnadu',
    'Parvathipuram Manyam': 'P. Manyam',
    'Prakasam': 'Prakasam',
    'Sri Potti Sriramulu Nellore': 'Nellore',
    'Sri Sathya Sai': 'Sathya Sai',
    'Srikakulam': 'Srikakulam',
    'Tirupati': 'Tirupati',
    'Visakhapatnam': 'Vizag',
    'Vizianagram': 'Vizianagaram',
    'West Godavari': 'W. Godavari',
    'YSR Kadapa': 'YSR Kadapa'
};

// Function to convert rating to color
const getRatingColor = (rating: number) => {
    if (!rating || rating < 1) return '#ef4444';
    const normalized = (rating - 1) / 4;
    const red = Math.round(34 + (239 - 34) * (1 - normalized));
    const green = Math.round(197 * normalized + 68 * (1 - normalized));
    const blue = Math.round(94 * normalized + 68 * (1 - normalized));
    return `rgb(${red}, ${green}, ${blue})`;
};

const YES_NO_PIE_COLORS = ['#16a34a', '#dc2626'];

const EMPTY_YES_NO_PIE: Array<{ name: 'Yes' | 'No'; value: number }> = [
    { name: 'Yes', value: 0 },
    { name: 'No', value: 0 },
];

function buildYesNoPieData(yesPercent: number, responses: number): Array<{ name: 'Yes' | 'No'; value: number }> {
    if (!responses || responses <= 0) {
        return [...EMPTY_YES_NO_PIE];
    }
    const yes = Math.round((Math.max(0, Math.min(100, yesPercent)) / 100) * responses);
    return [
        { name: 'Yes', value: yes },
        { name: 'No', value: Math.max(0, responses - yes) },
    ];
}

interface DashboardProps {
    filters: FilterOptions;
    onFiltersChange: (filters: FilterOptions) => void;
    onLogout: () => void;
}

const Dashboard: React.FC<DashboardProps> = ({ filters, onFiltersChange, onLogout }) => {
    const navigate = useNavigate();
    const { lastInvalidated, refreshDashboard } = useCache();
    const [selectedDistrict, setSelectedDistrict] = useState<string | null>(null);
    const [districtViewMode, setDistrictViewMode] = useState<'heatmap' | 'bar'>('heatmap');
    const [pendingFilters, setPendingFilters] = useState<FilterOptions>(filters);
    const [metrics, setMetrics] = useState<FeedbackDashboardMetrics | null>(null);
    const [loadingMetrics, setLoadingMetrics] = useState<boolean>(false);
    const [exporting, setExporting] = useState<boolean>(false);

    useEffect(() => {
        setPendingFilters(filters);
    }, [filters]);

    const resetFilters = () => ({
        workshops: [],
        districts: [],
        implementationPartners: [],
        dateRange: { start: '', end: '' }
    });

    const hasPendingFilters =
        pendingFilters.workshops.length > 0 ||
        pendingFilters.districts.length > 0 ||
        pendingFilters.implementationPartners.length > 0 ||
        pendingFilters.dateRange.start ||
        pendingFilters.dateRange.end;

    const hasPendingChanges = useMemo(() => {
        return JSON.stringify(pendingFilters) !== JSON.stringify(filters);
    }, [pendingFilters, filters]);

    // Fetch computed metrics from backend whenever applied filters change OR cache is invalidated
    useEffect(() => {
        let cancelled = false;
        setLoadingMetrics(true);

        (async () => {
            try {
                const m = await apiClient.getFeedbackDashboardMetrics({
                    startDate: filters.dateRange.start || undefined,
                    endDate: filters.dateRange.end || undefined,
                    workshopId: filters.workshops.length ? filters.workshops.join(',') : undefined,
                    district: filters.districts.length ? filters.districts.join(',') : undefined,
                    implementationPartnerId: filters.implementationPartners.length ? filters.implementationPartners.join(',') : undefined,
                });
                if (!cancelled) setMetrics(m);
            } catch {
                if (!cancelled) setMetrics(null);
            } finally {
                if (!cancelled) setLoadingMetrics(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [filters, lastInvalidated]); // Add lastInvalidated as dependency

    const analytics = useMemo(() => {
        if (!metrics) {
            return {
                participants: 0,
                totalMessagesSent: 0,
                total: 0,
                responseRate: 0,
                avgOverallRating: '0.0',
                highRatingSharePercentage: 0,
            };
        }
        return {
            participants: metrics.participants,
            totalMessagesSent: metrics.messagesSent,
            total: metrics.responses,
            responseRate: metrics.responseRate,
            avgOverallRating: (metrics.avgOverallRating || 0).toFixed(2),
            highRatingSharePercentage: metrics.highRatingSharePercentage,
        };
    }, [metrics]);

    const partnerBreakdown = useMemo(() => {
        return (metrics?.partnerBreakdown || []).map(p => ({
            partner: p.partnerId,
            total: p.total,
            avgOverallRating: (p.avgOverallRating || 0).toFixed(2),
        }));
    }, [metrics]);

    const districtBreakdown = useMemo(() => {
        return (metrics?.districtBreakdown || [])
            .map(d => ({
                district: d.district,
                total: d.total,
                shortName: districtShortNames[d.district] || d.district,
                avgOverallRating: Number((d.avgOverallRating || 0).toFixed(2)),
            }))
            .sort((a, b) => b.avgOverallRating - a.avgOverallRating);
    }, [metrics]);

    // Split districts for bar chart
    const districtColumns = useMemo(() => {
        const mid = Math.ceil(districtBreakdown.length / 2);
        return {
            col1: districtBreakdown.slice(0, mid),
            col2: districtBreakdown.slice(mid)
        };
    }, [districtBreakdown]);

    // Handle district click
    const handleDistrictClick = (district: string) => {
        if (selectedDistrict === district) {
            setSelectedDistrict(null);
            onFiltersChange({ ...filters, districts: [] });
        } else {
            setSelectedDistrict(district);
            onFiltersChange({ ...filters, districts: [district] });
        }
    };

    // Export filtered data to CSV
    const exportToCSV = async () => {
        setExporting(true);
        // Helper function to escape CSV values
        const escapeCSV = (value: string | number): string => {
            const str = String(value);
            // If value contains comma, quote, or newline, wrap in quotes and escape internal quotes
            if (str.includes(',') || str.includes('"') || str.includes('\n')) {
                return `"${str.replace(/"/g, '""')}"`;
            }
            // For text fields that might have spaces or special chars, wrap in quotes for consistency
            return str;
        };

        const headers = [
            'Candidate Name', 'Mobile', 'District', 'Workshop', 'Implementation Partner',
            'Batch ID', 'Start Date', 'End Date', 'Response Date',
            'Q1 (Content Easy)', 'Q2 (Added Value)', 'Q3 (Interaction)',
            'Q4 (Arrangements)', 'Q5 (Recommend)', 'Rating'
        ];

        try {
            const filteredData = await apiClient.getSurveyResponses({
                startDate: filters.dateRange.start || undefined,
                endDate: filters.dateRange.end || undefined,
                workshopId: filters.workshops.length ? filters.workshops.join(',') : undefined,
                district: filters.districts.length ? filters.districts.join(',') : undefined,
                implementationPartnerId: filters.implementationPartners.length ? filters.implementationPartners.join(',') : undefined,
            });

            const csvContent = [
                headers.join(','),
                ...filteredData.map(item => [
                    escapeCSV(item.candidateName),
                    escapeCSV(item.candidateMobile),
                    escapeCSV(item.district),
                    escapeCSV(getWorkshopName(item.workshopId)),
                    escapeCSV(getPartnerName(item.implementationPartnerId)),
                    escapeCSV(item.batchId),
                    escapeCSV(item.workshopStartDate),
                    escapeCSV(item.workshopEndDate),
                    escapeCSV(item.responseTimestamp),
                    escapeCSV(item.response.q1),
                    escapeCSV(item.response.q2),
                    escapeCSV(item.response.q3),
                    escapeCSV(item.response.q4),
                    escapeCSV(item.response.q5),
                    escapeCSV(item.response.rating)
                ].join(','))
            ].join('\n');

            const blob = new Blob([csvContent], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;

            // Generate filename with current date and filter info
            const dateStr = new Date().toISOString().split('T')[0];
            let filename = `msme-survey-data-${dateStr}`;
            if (filters.districts.length > 0) {
                filename += `-${filters.districts.length}districts`;
            }
            if (filters.workshops.length > 0) {
                filename += `-${filters.workshops.length}workshops`;
            }
            filename += '.csv';

            a.download = filename;
            a.click();
            window.URL.revokeObjectURL(url);
        } finally {
            setExporting(false);
        }
    };

    // Rating distribution for pie chart
    const ratingDistribution = useMemo(() => metrics?.ratingDistribution || [], [metrics]);

    const feedbackQ1ToQ5Pies = useMemo(() => {
        if (!metrics?.yesPercents) {
            return {
                q1: EMPTY_YES_NO_PIE,
                q2: EMPTY_YES_NO_PIE,
                q3: EMPTY_YES_NO_PIE,
                q4: EMPTY_YES_NO_PIE,
                q5: EMPTY_YES_NO_PIE,
            };
        }
        const r = metrics.responses ?? 0;
        const y = metrics.yesPercents;
        return {
            q1: buildYesNoPieData(y.q1, r),
            q2: metrics.addedValuePie ?? EMPTY_YES_NO_PIE,
            q3: buildYesNoPieData(y.q3, r),
            q4: buildYesNoPieData(y.q4, r),
            q5: metrics.recommendationPie ?? EMPTY_YES_NO_PIE,
        };
    }, [metrics]);

    // District Heat Map Component
    const DistrictHeatMap = ({ districtData, onDistrictClick, selectedDistrict }: any) => {
        const sortedDistricts = [...districtData].sort((a: any, b: any) => b.avgOverallRating - a.avgOverallRating);

        return (
            <div className="w-full">
                <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 mb-6">
                    {sortedDistricts.map((district: any) => (
                        <div
                            key={district.district}
                            className={`p-3 rounded-lg border text-center transition-all duration-300 hover:scale-105 cursor-pointer shadow-sm hover:shadow-md transform-gpu ${selectedDistrict === district.district ? 'ring-2 ring-blue-500 ring-offset-2' : ''
                                }`}
                            style={{
                                backgroundColor: getRatingColor(district.avgOverallRating),
                                color: '#ffffff',
                                textShadow: '0 1px 2px rgba(0,0,0,0.2)'
                            }}
                            onClick={() => onDistrictClick && onDistrictClick(district.district)}
                            title={`${district.district}: ${district.avgOverallRating}/5 (${district.total} responses)`}
                        >
                            <div className="text-xs font-bold mb-1">{district.shortName}</div>
                            <div className="text-sm font-bold">{district.avgOverallRating}</div>
                            <div className="text-xs opacity-75">{district.total}</div>
                        </div>
                    ))}
                </div>
                <div className="mt-6 p-4 bg-slate-50 rounded-lg">
                    <h4 className="text-sm font-bold text-slate-600 mb-3 flex items-center gap-2">
                        <Target size={16} />
                        Rating Scale Legend
                    </h4>
                    <div className="flex items-center gap-4 flex-wrap">
                        {[1, 2, 3, 4, 5].map(r => (
                            <div key={r} className="flex items-center gap-2">
                                <div
                                    style={{ backgroundColor: getRatingColor(r) }}
                                    className="w-5 h-5 rounded-md border border-slate-300 shadow-sm"
                                ></div>
                                <span className="text-sm text-slate-600 font-medium">{r}</span>
                            </div>
                        ))}
                        <span className="text-sm text-slate-500 ml-4 font-medium">(1 = Poor, 5 = Excellent)</span>
                    </div>
                </div>
            </div>
        );
    };

    // Bar Chart for District Performance
    const DistrictBarChart = ({ districtColumns, selectedDistrict, handleDistrictBarClick }: any) => (
        <div className="w-full">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
                <ResponsiveContainer width="100%" height={districtColumns.col1.length * 32}>
                    <BarChart
                        data={districtColumns.col1}
                        layout="vertical"
                        margin={{ top: 0, right: 30, left: 80, bottom: 0 }}
                        barSize={16}
                    >
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" opacity={0.7} />
                        <XAxis type="number" dataKey="avgOverallRating" domain={[0, 5]} hide />
                        <YAxis type="category" dataKey="shortName" tick={{ fontSize: 11, fill: '#64748b' }} width={80} interval={0} />
                        <Tooltip />
                        <Bar
                            dataKey="avgOverallRating"
                            name="Avg Overall Rating"
                            radius={[0, 6, 6, 0]}
                            cursor="pointer"
                            onClick={(data: any) => handleDistrictBarClick(data.district)}
                        >
                            <LabelList
                                dataKey="avgOverallRating"
                                position="right"
                                style={{ fill: '#1f2937', fontSize: 11, fontWeight: 'bold' }}
                            />
                            {districtColumns.col1.map((entry: any, index: number) => (
                                <Cell
                                    key={`cell-dist1-${index}`}
                                    fill={getRatingColor(entry.avgOverallRating)}
                                    className={`transition-all duration-200 hover:brightness-110 ${selectedDistrict === entry.district ? 'brightness-90 stroke-blue-500 stroke-2' : ''
                                        }`}
                                />
                            ))}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>

                <ResponsiveContainer width="100%" height={districtColumns.col2.length * 32}>
                    <BarChart
                        data={districtColumns.col2}
                        layout="vertical"
                        margin={{ top: 0, right: 30, left: 80, bottom: 0 }}
                        barSize={16}
                    >
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" opacity={0.7} />
                        <XAxis type="number" dataKey="avgOverallRating" domain={[0, 5]} hide />
                        <YAxis type="category" dataKey="shortName" tick={{ fontSize: 11, fill: '#64748b' }} width={80} interval={0} />
                        <Tooltip />
                        <Bar
                            dataKey="avgOverallRating"
                            name="Avg Overall Rating"
                            radius={[0, 6, 6, 0]}
                            cursor="pointer"
                            onClick={(data: any) => handleDistrictBarClick(data.district)}
                        >
                            <LabelList
                                dataKey="avgOverallRating"
                                position="right"
                                style={{ fill: '#1f2937', fontSize: 11, fontWeight: 'bold' }}
                            />
                            {districtColumns.col2.map((entry: any, index: number) => (
                                <Cell
                                    key={`cell-dist2-${index}`}
                                    fill={getRatingColor(entry.avgOverallRating)}
                                    className={`transition-all duration-200 hover:brightness-110 ${selectedDistrict === entry.district ? 'brightness-90 stroke-blue-500 stroke-2' : ''
                                        }`}
                                />
                            ))}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </div>
            <p className="mt-4 text-xs text-slate-500 italic">Click on any district bar to filter data for that district</p>
        </div>
    );

    return (
        <div className="min-h-screen bg-slate-100 p-2 md:p-4 font-sans flex flex-col" aria-busy={loadingMetrics || exporting}>
            <LoadingOverlay
                show={loadingMetrics || exporting}
                label={exporting ? 'Preparing export…' : 'Loading dashboard…'}
            />
            {/* Enhanced Header */}
            <header className="mb-4">
                <div className="bg-white rounded-xl shadow-sm p-4 border border-slate-200 transition-all duration-300 hover:shadow-md">
                    <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-2">
                        <div className="flex items-center gap-4">
                            <div className="flex items-center gap-3">
                                <PortalLogoLink />
                            </div>
                            <div>
                                <h1 className="text-xl md:text-2xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent">
                                    RAMP Feedback Dashboard
                                </h1>
                                <p className="text-slate-500 mt-1 text-sm">
                                    Andhra Pradesh - Real-time WhatsApp Feedback Analytics
                                </p>
                            </div>
                        </div>
                        <div className="flex gap-2 w-full md:w-auto">
                            <button
                                onClick={() => navigate('/ramp-me')}
                                className="flex items-center gap-2 bg-gradient-to-r from-[#0D52BD] to-[#0a3d8f] text-white px-4 py-2 rounded-lg hover:from-[#0a3d8f] hover:to-blue-900 transition-all duration-200 shadow-sm hover:shadow-md text-sm flex-1 md:flex-none"
                            >
                                <Target size={16} />
                                M&E Dashboard
                            </button>
                            <button
                                onClick={() => navigate('/partners')}
                                className="flex items-center gap-2 bg-gradient-to-r from-purple-600 to-purple-700 text-white px-4 py-2 rounded-lg hover:from-purple-700 hover:to-purple-800 transition-all duration-200 shadow-sm hover:shadow-md text-sm flex-1 md:flex-none"
                            >
                                <Users size={16} />
                                Partner Analytics
                            </button>
                            <button
                                onClick={() => navigate('/departments')}
                                className="flex items-center gap-2 bg-gradient-to-r from-green-600 to-green-700 text-white px-4 py-2 rounded-lg hover:from-green-700 hover:to-green-800 transition-all duration-200 shadow-sm hover:shadow-md text-sm flex-1 md:flex-none"
                            >
                                <Building2 size={16} />
                                Department Analytics
                            </button>
                            <button
                                onClick={exportToCSV}
                                disabled={exporting || loadingMetrics}
                                className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white px-4 py-2 rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-200 shadow-sm hover:shadow-md text-sm flex-1 md:flex-none"
                            >
                                <Download size={16} />
                                {exporting ? 'Exporting…' : 'Export Data'}
                            </button>
                            <button
                                onClick={refreshDashboard}
                                disabled={loadingMetrics}
                                className="flex items-center gap-2 bg-gradient-to-r from-indigo-600 to-indigo-700 text-white px-4 py-2 rounded-lg hover:from-indigo-700 hover:to-indigo-800 transition-all duration-200 shadow-sm hover:shadow-md text-sm flex-1 md:flex-none disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <RefreshCw size={16} className={loadingMetrics ? 'animate-spin' : ''} />
                                {loadingMetrics ? 'Refreshing...' : 'Refresh Data'}
                            </button>
                            <button
                                onClick={onLogout}
                                className="flex items-center gap-2 bg-gradient-to-r from-red-600 to-red-700 text-white px-4 py-2 rounded-lg hover:from-red-700 hover:to-red-800 transition-all duration-200 shadow-sm hover:shadow-md text-sm flex-1 md:flex-none"
                            >
                                <LogOut size={16} />
                                Logout
                            </button>
                        </div>
                    </div>
                </div>
            </header>

            {/* Filters Section */}
            <section className="mb-4">
                <Filters
                    filters={pendingFilters}
                    onFiltersChange={setPendingFilters}
                    showResetButton={false}
                    footer={
                        <div className="flex flex-col sm:flex-row gap-2 justify-end">
                            <button
                                onClick={() => {
                                    const next = resetFilters();
                                    setPendingFilters(next);
                                    onFiltersChange(next);
                                }}
                                disabled={!hasPendingFilters && !hasPendingChanges}
                                className="px-4 py-2 rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-semibold"
                            >
                                Reset Filters
                            </button>
                            <button
                                onClick={() => onFiltersChange(pendingFilters)}
                                disabled={!hasPendingChanges || loadingMetrics}
                                className="px-4 py-2 rounded-lg bg-gradient-to-r from-[#0D52BD] to-[#0a3d8f] text-white hover:from-[#0a3d8f] hover:to-blue-900 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-semibold shadow-sm hover:shadow-md"
                            >
                                {loadingMetrics ? 'Loading…' : 'Apply Filters'}
                            </button>
                        </div>
                    }
                />
            </section>

            {/* Enhanced KPI Cards */}
            <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4 mb-4">
                <div className="bg-white rounded-xl shadow-sm p-4 flex items-center justify-between border border-slate-200 transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
                    <div>
                        <p className="text-sm text-slate-500 font-medium">Participants</p>
                        <p className="text-2xl font-bold text-slate-800">
                            {analytics.participants.toLocaleString()}
                        </p>
                    </div>
                    <div className="bg-indigo-100 p-3 rounded-full">
                        <Users className="h-6 w-6 text-indigo-600" />
                    </div>
                </div>
                <div className="bg-white rounded-xl shadow-sm p-4 flex items-center justify-between border border-slate-200 transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
                    <div>
                        <p className="text-sm text-slate-500 font-medium">Messages Sent</p>
                        <p className="text-2xl font-bold text-slate-800">
                            {analytics.totalMessagesSent.toLocaleString()}
                        </p>
                    </div>
                    <div className="bg-gray-100 p-3 rounded-full">
                        <Send className="h-6 w-6 text-gray-600" />
                    </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm p-4 flex items-center justify-between border border-slate-200 transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
                    <div>
                        <p className="text-sm text-slate-500 font-medium">Responses</p>
                        <p className="text-2xl font-bold text-slate-800">
                            {analytics.total.toLocaleString()}
                        </p>
                    </div>
                    <div className="bg-blue-100 p-3 rounded-full">
                        <Users className="h-6 w-6 text-blue-600" />
                    </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm p-4 flex items-center justify-between border border-slate-200 transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
                    <div>
                        <p className="text-sm text-slate-500 font-medium">Response Rate</p>
                        <p className="text-2xl font-bold text-green-600">
                            {analytics.responseRate.toFixed(1)}%
                        </p>
                    </div>
                    <div className="bg-green-100 p-3 rounded-full">
                        <Percent className="h-6 w-6 text-green-600" />
                    </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm p-4 flex items-center justify-between border border-slate-200 transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
                    <div>
                        <p className="text-sm text-slate-500 font-medium">Avg Overall Rating</p>
                        <p className="text-2xl font-bold" style={{ color: getRatingColor(parseFloat(analytics.avgOverallRating)) }}>
                            {analytics.avgOverallRating}/5
                        </p>
                    </div>
                    <div className="bg-yellow-100 p-3 rounded-full">
                        <Star className="h-6 w-6 text-yellow-500" />
                    </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm p-4 flex items-center justify-between border border-slate-200 transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
                    <div>
                        <p className="text-sm text-slate-500 font-medium">High Rating Share (4–5)</p>
                        <p className="text-2xl font-bold text-indigo-600">
                            {analytics.highRatingSharePercentage.toFixed(1)}%
                        </p>
                    </div>
                    <div className="bg-indigo-100 p-3 rounded-full">
                        <TrendingUp className="h-6 w-6 text-indigo-600" />
                    </div>
                </div>
            </section>

            {/* Main Content */}
            <main className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Left Column - 2/3 width */}
                <div className="lg:col-span-2 space-y-4">
                    {/* Feedback Received Card */}
                    <section className="bg-white rounded-xl shadow-sm p-6 border border-slate-200 transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="p-2 bg-blue-100 rounded-lg">
                                <BarChart2 className="h-5 w-5 text-blue-600" />
                            </div>
                            <h3 className="text-lg font-semibold text-slate-700">Feedback Received</h3>
                        </div>

                        {/* All six feedback questions as pie charts (Q1–Q5 Yes/No, Q6 overall rating) */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {[
                                { key: 'q1', title: 'Content easy to understand', data: feedbackQ1ToQ5Pies.q1 },
                                { key: 'q2', title: 'Added value to enterprise', data: feedbackQ1ToQ5Pies.q2 },
                                { key: 'q3', title: 'Encouraged participant interaction', data: feedbackQ1ToQ5Pies.q3 },
                                { key: 'q4', title: 'Arrangements satisfactory', data: feedbackQ1ToQ5Pies.q4 },
                                { key: 'q5', title: 'Would recommend', data: feedbackQ1ToQ5Pies.q5 },
                            ].map(({ key, title, data }) => (
                                <div key={key} className="w-full">
                                    <div className="relative pt-10">
                                        <div className="absolute top-0 z-10 left-1/2 transform -translate-x-1/2">
                                            <h4 className="text-sm text-slate-600 font-bold bg-white px-3 py-1 rounded-full shadow-sm border border-slate-200 text-center max-w-[min(100%,14rem)] leading-snug">
                                                {title}
                                            </h4>
                                        </div>
                                        <ResponsiveContainer width="100%" height={260}>
                                            <PieChart>
                                                <Pie
                                                    data={data}
                                                    dataKey="value"
                                                    nameKey="name"
                                                    cx="50%"
                                                    cy="45%"
                                                    outerRadius={80}
                                                    paddingAngle={3}
                                                    labelLine={false}
                                                >
                                                    {data.map((_, index) => (
                                                        <Cell
                                                            key={`cell-${key}-${index}`}
                                                            fill={YES_NO_PIE_COLORS[index % 2]}
                                                        />
                                                    ))}
                                                </Pie>
                                                <Tooltip />
                                                <Legend
                                                    iconType="circle"
                                                    layout="horizontal"
                                                    verticalAlign="bottom"
                                                    align="center"
                                                    wrapperStyle={{ fontSize: '11px' }}
                                                />
                                            </PieChart>
                                        </ResponsiveContainer>
                                    </div>
                                </div>
                            ))}

                            <div className="w-full">
                                <div className="relative pt-10">
                                    <div className="absolute top-0 z-10 left-1/2 transform -translate-x-1/2">
                                        <h4 className="text-sm text-slate-600 font-bold bg-white px-3 py-1 rounded-full shadow-sm border border-slate-200">
                                            Overall rating distribution
                                        </h4>
                                    </div>
                                    <ResponsiveContainer width="100%" height={260}>
                                        <PieChart>
                                            <Pie
                                                data={ratingDistribution}
                                                dataKey="value"
                                                nameKey="name"
                                                cx="50%"
                                                cy="45%"
                                                innerRadius={50}
                                                outerRadius={80}
                                                paddingAngle={3}
                                                labelLine={false}
                                            >
                                                {ratingDistribution.map((_, index) => (
                                                    <Cell
                                                        key={`cell-rating-${index}`}
                                                        fill={['#dc2626', '#ea580c', '#d97706', '#16a34a', '#059669'][index % 5]}
                                                    />
                                                ))}
                                            </Pie>
                                            <Tooltip />
                                            <Legend
                                                iconType="circle"
                                                layout="horizontal"
                                                verticalAlign="bottom"
                                                align="center"
                                                wrapperStyle={{ fontSize: '10px' }}
                                            />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* District Performance Card */}
                    {filters.districts.length === 0 && (
                        <section className="bg-white rounded-xl shadow-sm p-4 border border-slate-200 transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
                            <div className="flex items-center justify-between mb-6">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-green-100 rounded-lg">
                                        <Users className="h-5 w-5 text-green-600" />
                                    </div>
                                    <h3 className="text-lg font-semibold text-slate-700">District Performance</h3>
                                </div>

                                {/* Enhanced Toggle UI */}
                                <div className="bg-slate-100 rounded-lg p-1 border border-slate-200 shadow-sm">
                                    <div className="flex items-center">
                                        <button
                                            onClick={() => setDistrictViewMode('heatmap')}
                                            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-all duration-200 ${districtViewMode === 'heatmap'
                                                ? 'bg-white text-slate-900 shadow-sm border border-slate-200'
                                                : 'text-slate-600 hover:text-slate-900 hover:bg-white/50'
                                                }`}
                                        >
                                            <Grid3X3 size={16} />
                                            Heat Map
                                        </button>
                                        <button
                                            onClick={() => setDistrictViewMode('bar')}
                                            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-all duration-200 ${districtViewMode === 'bar'
                                                ? 'bg-white text-slate-900 shadow-sm border border-slate-200'
                                                : 'text-slate-600 hover:text-slate-900 hover:bg-white/50'
                                                }`}
                                        >
                                            <BarChart2 size={16} />
                                            Bar Chart
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {districtViewMode === 'heatmap' ? (
                                <DistrictHeatMap
                                    districtData={districtBreakdown}
                                    onDistrictClick={handleDistrictClick}
                                    selectedDistrict={selectedDistrict}
                                />
                            ) : (
                                <DistrictBarChart
                                    districtColumns={districtColumns}
                                    selectedDistrict={selectedDistrict}
                                    handleDistrictBarClick={handleDistrictClick}
                                />
                            )}
                        </section>
                    )}
                </div>

                {/* Right Column - 1/3 width */}
                <div className="space-y-4">
                    {/* Implementation Partner Performance Summary */}
                    <section className="bg-white rounded-xl shadow-sm p-4 border border-slate-200 transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="p-2 bg-purple-100 rounded-lg">
                                <Award className="h-5 w-5 text-purple-600" />
                            </div>
                            <h3 className="text-lg font-semibold text-slate-700">Implementation Partner Performance Summary</h3>
                        </div>

                        <div className="space-y-3">
                            {partnerBreakdown.map((item: any, index: number) => (
                                <div key={item.partner} className="text-sm p-4 bg-slate-50 hover:bg-slate-100 rounded-lg transition-all duration-200 cursor-pointer border border-transparent hover:border-slate-200 hover:shadow-sm">
                                    <div className="flex justify-between items-center">
                                        <div>
                                            <p className="font-bold text-slate-800">{getPartnerName(item.partner)}</p>
                                            <p className="text-xs text-slate-500 mt-1">{item.total} responses</p>
                                        </div>
                                        <div className="flex items-center gap-1 font-bold text-lg" style={{ color: getRatingColor(parseFloat(item.avgOverallRating)) }}>
                                            <Star size={16} fill="currentColor" />
                                            <span>{item.avgOverallRating}</span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>
                </div>
            </main>

            {/* Footer */}
            <Footer />
        </div>
    );
};

export default Dashboard;
